import { useEffect, useState, useRef } from "react";
import { useValue } from "@tldraw/state-react";
import type { Editor } from "./canvas";
import type { TLShapeId } from "@tldraw/tlschema";
import {
  activeTrace,
  annotationTargets,
  editableTarget,
  installTraceAnnotations,
} from "./tracing";
import { notify } from "./api";
export function useTraceAnnotation(editor: Editor) {
  const lastDrawTool = useRef("draw");
  const [id, setId] = useState<TLShapeId | null>(null);
  const trace = useValue(
    "active trace",
    () => (id ? editor.getShape(id) : undefined),
    [editor, id],
  );
  const allowed = useValue(
    "trace is editable",
    () => (id ? editableTarget(editor, editor.getShape(id)) : false),
    [editor, id],
  );
  const end = () => {
    const tool = editor.getCurrentToolId();
    if (
      annotationTargets.has(editor) &&
      [
        "draw",
        "geo",
        "text",
        "arrow",
        "line",
        "polygon",
        "area",
        "dimension",
        "angle",
        "eraser",
      ].includes(tool)
    )
      lastDrawTool.current = tool;
    annotationTargets.delete(editor);
    setId(null);
    editor.setCurrentTool("select");
  };
  useEffect(() => installTraceAnnotations(editor), [editor]);
  useEffect(() => {
    if (id && !allowed) {
      end();
      notify("Annotation stopped: tracing paper is hidden, locked or deleted.");
    }
  }, [allowed, editor, id]);
  useEffect(() => {
    const escape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && annotationTargets.has(editor)) end();
    };
    window.addEventListener("keydown", escape);
    return () => {
      window.removeEventListener("keydown", escape);
      annotationTargets.delete(editor);
    };
  }, [editor]);
  return {
    trace: trace?.type === "trace" ? trace : undefined,
    begin: (id: TLShapeId, fresh = false) => {
      if (fresh) lastDrawTool.current = "draw";
      annotationTargets.set(editor, id);
      if (!activeTrace(editor)) {
        annotationTargets.delete(editor);
        return;
      }
      setId(id);
      editor.selectNone();
      editor.setCurrentTool(lastDrawTool.current);
      editor.focus();
    },
    end,
  };
}
